import { useLanguageStore } from '@/stores/LanguageStore'

import { PERMISSIONS, UserPermission, allPermissionsList } from './enums/permissions'

export enum UserRole {
  Admin = 1,
  Operator = 2,
  User = 3,
  Sap = 4,
  Erector = 5,
  Manager = 6,
  Monitor = 7,
  Root = 8,
  // Tudo de Root, mais o total da licenca. Nunca atribuida pelo frontend: so o usuario
  // padrao a tem, dada pelo backend no startup.
  Nepen = 9,
}

/** Permissões que só a role Nepen tem; Root recebe todas as outras. */
const NEPEN_ONLY_PERMISSIONS: UserPermission[] = [PERMISSIONS.MANAGE.LICENSE_TOTAL_MANAGE]

/**
 * A tela de licença não vem da role: é da Nepen e de quem ela liberou (`acessoLicenca`), de
 * qualquer role. A API aplica o mesmo pela policy "Licenca".
 */
const LICENSE_PERMISSIONS: UserPermission[] = [
  PERMISSIONS.MANAGE.LICENSE_MANAGE,
  PERMISSIONS.MANAGE.LICENSE_TOTAL_MANAGE,
]

export enum AuthUserType {
  Normal = 1,
  LDAP = 2,
}

export interface IUser {
  id?: string
  description?: string
  name?: string
  email?: string
  username?: string
  password?: string
  clientType?: string | number
  userRole?: UserRole
  userType?: AuthUserType
  blocked?: boolean
  updatedAt?: Date
  receiveEmails?: boolean
  isPasswordAlteredOnce?: boolean
  /** Liberado pela Nepen para a tela de licença. Só a Nepen vê e altera este campo. */
  acessoLicenca?: boolean
}

export const toUserRole = (value: any): UserRole => Number(value) as UserRole
export const toAuthUserType = (value: any): AuthUserType => Number(value) as AuthUserType

export const isRole = (value: any, role: UserRole): boolean => toUserRole(value) === role
export const isAuthType = (value: any, type: AuthUserType): boolean =>
  toAuthUserType(value) === type

export const getUserRoleLabel = (role?: any) => {
  const { t } = useLanguageStore()

  switch (toUserRole(role)) {
    case UserRole.Admin:
      return t('auth.userType.admin')
    case UserRole.Operator:
      return t('auth.userType.operator')
    case UserRole.User:
      return t('auth.userType.user')
    case UserRole.Sap:
      return t('auth.userType.sap')
    case UserRole.Erector:
      return t('auth.userType.erector')
    case UserRole.Manager:
      return t('auth.userType.manager')
    case UserRole.Monitor:
      return t('auth.userType.monitor')
    case UserRole.Root:
      return t('auth.userType.root')
    case UserRole.Nepen:
      // Nome proprio, igual nos tres idiomas.
      return 'NEPEN'
    default:
      return t('auth.userType.invalid')
  }
}

/** Root ou Nepen: Nepen tem tudo o que Root tem. */
export const hasRootAccess = (value: any): boolean =>
  isRole(value, UserRole.Root) || isRole(value, UserRole.Nepen)

const storedUserRole = (): any => {
  const stored = localStorage.getItem('user')
  if (!stored) return undefined
  try {
    return JSON.parse(stored)?.userRole
  } catch {
    return undefined
  }
}

/** Verdadeiro para Root e para Nepen, que herda tudo de Root. */
export const isRoot = (): boolean => hasRootAccess(storedUserRole())

export const isNepen = (): boolean => isRole(storedUserRole(), UserRole.Nepen)

export const canResendCcee = (): boolean => {
  const role = storedUserRole()
  return isRole(role, UserRole.Admin) || isRole(role, UserRole.Operator) || hasRootAccess(role)
}

export class IUserDTO implements IUser {
  public id?: string
  public description: string
  public name: string
  public email: string
  public username?: string
  public password: string
  public clientType?: string | number
  public userRole: UserRole
  public userType: AuthUserType
  public blocked: boolean
  public updatedAt: Date
  public receiveEmails?: boolean
  public acessoLicenca?: boolean

  constructor(
    description: string = '',
    name: string = '',
    email: string = '',
    password: string = '',
    clientType: string | number = 0,
    userRole: UserRole = UserRole.User,
    blocked: boolean = false,
    updatedAt: Date = new Date(),
    receiveEmails: boolean = false,
    userType: AuthUserType = AuthUserType.Normal,
  ) {
    this.description = description
    this.name = name
    this.email = email
    this.password = password
    this.clientType = clientType
    this.userRole = userRole
    this.userType = userType
    this.blocked = blocked
    this.updatedAt = updatedAt
    this.receiveEmails = receiveEmails
  }

  get permissions(): UserPermission[] {
    const daRole = this.rolePermissions().filter((p) => !LICENSE_PERMISSIONS.includes(p))

    if (this.userRole === UserRole.Nepen) return [...daRole, ...LICENSE_PERMISSIONS]
    if (this.acessoLicenca) return [...daRole, PERMISSIONS.MANAGE.LICENSE_MANAGE]
    return daRole
  }

  private rolePermissions(): UserPermission[] {
    switch (this.userRole) {
      case UserRole.Admin:
        return allPermissionsList.filter(
          (p) =>
            p !== PERMISSIONS.PAGES.MANAGEMENT_CLIENTS_VIEW && !NEPEN_ONLY_PERMISSIONS.includes(p),
        )

      case UserRole.Manager:
        return [
          PERMISSIONS.MANAGE.DISPLAY_CONFIG_MANAGE,
          PERMISSIONS.MANAGE.CREATE_MANAGE,
          ...Object.values(PERMISSIONS.PAGES).filter(
            (p) => p !== PERMISSIONS.PAGES.MANAGEMENT_CLIENTS_VIEW,
          ),
          ...Object.values(PERMISSIONS.TABLES),
          ...Object.values(PERMISSIONS.COMMANDS),
        ]

      case UserRole.Operator:
      case UserRole.Monitor: {
        const permissions: UserPermission[] = [
          PERMISSIONS.PAGES.DASHBOARD_VIEW,
          PERMISSIONS.PAGES.METER_VIEW,
          PERMISSIONS.PAGES.SMC_VIEW,
          PERMISSIONS.PAGES.DOWNLOAD_VIEW,
          PERMISSIONS.PAGES.MANAGEMENT_VIEW,
          PERMISSIONS.PAGES.MANAGEMENT_GROUPS_VIEW,
          PERMISSIONS.PAGES.MANAGEMENT_TAGS_VIEW,
          PERMISSIONS.PAGES.SERVICE_NOTES_VIEW,
          PERMISSIONS.PAGES.PHOTOC_VIEW,
          PERMISSIONS.PAGES.BILLING_VIEW,
          PERMISSIONS.MANAGE.DOWNLOAD_MANAGE,
          PERMISSIONS.TABLES.FROZEN_DATA_VIEW,
          PERMISSIONS.TABLES.MASS_MEMORY_VIEW,
          PERMISSIONS.TABLES.ALARM_VIEW,
          PERMISSIONS.TABLES.EVENT_VIEW,
          PERMISSIONS.TABLES.TICKETS_VIEW,
          PERMISSIONS.TABLES.COMMAND_VIEW,
          PERMISSIONS.TABLES.INSTANT_MEASURE_VIEW,
          PERMISSIONS.TABLES.QEE_VIEW,
          PERMISSIONS.COMMANDS.EXECUTE_COMMANDS,
          PERMISSIONS.COMMANDS.EXECUTE_RELAY_STATE,
          PERMISSIONS.COMMANDS.EXECUTE_UPDATE_DATETIME,
          PERMISSIONS.COMMANDS.EXECUTE_ENERGY_TOTALIZERS,
          PERMISSIONS.COMMANDS.EXECUTE_MASS_MEMORY,
          PERMISSIONS.COMMANDS.EXECUTE_INSTANTANEOUS_READINGS,
          PERMISSIONS.COMMANDS.EXECUTE_QUALITY_PARAMETERS,
          PERMISSIONS.COMMANDS.EXECUTE_GET_RELAY,
          PERMISSIONS.COMMANDS.EXECUTE_CUT_RECONNECT,
          PERMISSIONS.COMMANDS.EXECUTE_SET_RELAY_OFF,
          PERMISSIONS.COMMANDS.EXECUTE_SET_RELAY_ON,
          PERMISSIONS.MANAGE.DISPLAY_CONFIG_MANAGE,
        ]

        if (this.userRole === UserRole.Operator) {
          permissions.push(
            PERMISSIONS.COMMANDS.REQUEST_DIC_FIC_GROUP,
            PERMISSIONS.COMMANDS.EXECUTE_QEE_BATCH_QUALITY_READ_BY_GROUP,
          )
        }

        return permissions
      }

      case UserRole.User:
        return [
          PERMISSIONS.PAGES.METER_VIEW,
          PERMISSIONS.PAGES.SMC_VIEW,
          PERMISSIONS.PAGES.DOWNLOAD_VIEW,
          PERMISSIONS.PAGES.MANAGEMENT_VIEW,
          PERMISSIONS.PAGES.MANAGEMENT_GROUPS_VIEW,
          PERMISSIONS.PAGES.EFFECTIVENESSANDAVAILABILITY_VIEW,
          PERMISSIONS.PAGES.QUALITYPARAMETERS_VIEW,
          PERMISSIONS.MANAGE.DOWNLOAD_MANAGE,
          PERMISSIONS.TABLES.FROZEN_DATA_VIEW,
          PERMISSIONS.TABLES.MASS_MEMORY_VIEW,
          PERMISSIONS.TABLES.ALARM_VIEW,
          PERMISSIONS.TABLES.EVENT_VIEW,
          PERMISSIONS.TABLES.TICKETS_VIEW,
          PERMISSIONS.TABLES.COMMAND_VIEW,
        ]
      case UserRole.Root:
        return allPermissionsList.filter((p) => !NEPEN_ONLY_PERMISSIONS.includes(p))

      case UserRole.Nepen:
        return allPermissionsList

      case UserRole.Sap:
        return []
      case UserRole.Erector:
        return []

      default:
        return []
    }
  }
}
